import { formatPesoAmount } from '../utils/money';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiChevronDown, FiChevronRight, FiExternalLink, FiSearch } from 'react-icons/fi';
import { useSearchParams } from 'react-router-dom';
import {
  getComplexPriceRules,
  getPriceListConfigurations,
  updatePriceListFormatSettings,
  updateProductPriceListConfiguration,
  updateVarietyPriceListConfiguration,
} from '../api';
import {
  findComplexPriceRule,
  formatComplexRangeLabel,
  normalizePriceRuleSelection,
  resolvePriceRuleSelection,
} from '../utils/complexPriceRules';
import InfoTooltip from './InfoTooltip';
import InlineLoading from './InlineLoading';
import useProductEvents from '../hooks/useProductEvents';
import usePurchaseEvents from '../hooks/usePurchaseEvents';

const STOCK_RULE_MODES = ['markup_amount', 'markup_percent', 'fixed_price'];
const isComplexRuleMode = (mode) => String(mode || '').startsWith('complex:');

const toNullableNumber = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toInputValue = (value) => (
  value === null || value === undefined || !Number.isFinite(Number(value))
    ? ''
    : String(value)
);

const normalizeBasis = (value) => (
  String(value || '').trim().toLowerCase() === 'kg' ? 'kg' : 'unit'
);

const basisLabel = (basis) => (normalizeBasis(basis) === 'kg' ? 'por kg' : 'por unidad de venta');

const currency = (value) => {
  const parsed = toNullableNumber(value);
  return parsed === null
    ? 'Sin dato'
    : `$${formatPesoAmount(Math.round(parsed))}`;
};

const sourceLabel = (source) => {
  const labels = {
    manual_fallback: 'Costo de respaldo manual',
    manual: 'Costo de respaldo manual',
    purchase: 'Última compra válida',
    latest_purchase: 'Última compra válida',
    stock_list: 'Lista de stock activa',
    active_stock_list: 'Lista de stock activa',
  };
  return labels[String(source || '').trim().toLowerCase()] || 'Fuente calculada por el sistema';
};

export const normalizeAdvancedPricingCatalog = (response) => (
  (Array.isArray(response?.data) ? response.data : []).map((product) => ({
    id: product.id,
    name: product.productName || product.name || 'Producto sin nombre',
    image: product.productImage || product.image || null,
    categoryName: product.category?.name || product.Category?.name || 'Sin categoría',
    pricingBasisOverride: product.pricing?.pricingBasisOverride ?? product.pricingBasisOverride ?? null,
    effectivePricingBasis: normalizeBasis(
      product.pricing?.effectivePricingBasis || product.effectivePricingBasis
    ),
    varieties: (product.varieties || product.Varieties || []).map((variety) => ({
      id: variety.id,
      name: variety.varietyName || variety.name || 'Variedad sin nombre',
      priceAutoRuleOverride: variety.pricing?.priceAutoRuleOverride
        ?? variety.priceAutoRuleOverride
        ?? null,
      formats: (variety.formats || variety.VarietyPrices || []).map((format) => {
        const identity = format.identity || format;
        const shared = format.shared || format.pricingConfiguration?.shared || {};
        const branch = format.branch || format.pricingConfiguration?.branch || {};
        return {
          varietyPriceId: format.varietyPriceId ?? format.id,
          identity,
          shared,
          branch,
          costs: format.costs || format.pricingConfiguration?.costs || null,
          analysis: format.analysis || format.pricingConfiguration?.analysis || null,
        };
      }),
    })),
  }))
);

export const buildAdvancedPricingSettingsDraft = (format = {}) => {
  const shared = format.shared || {};
  const selection = normalizePriceRuleSelection(
    format.branch?.priceRule,
    format.branch?.stockPriceRule
  );
  const rule = selection?.type === 'simple' ? selection.rule : {};
  const stockPriceRuleMode = selection?.type === 'complex'
    ? `complex:${selection.complexPriceRuleId}`
    : STOCK_RULE_MODES.includes(rule.mode) ? rule.mode : '';

  return {
    manualBaseCost: toInputValue(shared.manualBaseCost),
    stockPriceRuleMode,
    stockPriceRuleValue: stockPriceRuleMode ? toInputValue(rule.value) : '',
  };
};

const validateNonNegativeNumber = (
  value,
  { required = false, percent = false, integer = false } = {}
) => {
  if (value === '' || value === null || value === undefined) {
    return required ? 'Ingresa un valor.' : '';
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 'Ingresa un número válido.';
  if (parsed < 0) return 'El valor debe ser mayor o igual a 0.';
  if (integer && !Number.isInteger(parsed)) return 'Ingresa un número entero.';
  if (percent && parsed > 100) return 'El porcentaje no puede superar 100.';
  return '';
};

export const validateAdvancedPricingSettingsDraft = (draft = {}) => {
  const errors = {};
  const manualError = validateNonNegativeNumber(draft.manualBaseCost, { integer: true });
  if (manualError) errors.manualBaseCost = manualError;

  const complexPriceRuleId = isComplexRuleMode(draft.stockPriceRuleMode)
    ? Number(String(draft.stockPriceRuleMode).slice('complex:'.length))
    : null;
  if (isComplexRuleMode(draft.stockPriceRuleMode)
    && (!Number.isSafeInteger(complexPriceRuleId) || complexPriceRuleId <= 0)) {
    errors.stockPriceRuleMode = 'Selecciona una regla compleja válida.';
  } else if (draft.stockPriceRuleMode
    && !STOCK_RULE_MODES.includes(draft.stockPriceRuleMode)
    && !isComplexRuleMode(draft.stockPriceRuleMode)) {
    errors.stockPriceRuleMode = 'Selecciona una regla válida.';
  } else if (draft.stockPriceRuleMode && !isComplexRuleMode(draft.stockPriceRuleMode)) {
    const valueError = validateNonNegativeNumber(draft.stockPriceRuleValue, {
      required: true,
      integer: draft.stockPriceRuleMode !== 'markup_percent',
    });
    if (valueError) errors.stockPriceRuleValue = valueError;
  } else if (draft.stockPriceRuleValue !== '' && draft.stockPriceRuleValue !== null && draft.stockPriceRuleValue !== undefined) {
    errors.stockPriceRuleMode = 'Selecciona una regla de precio.';
  }

  return errors;
};

export const buildAdvancedPricingSettingsPayload = (draft = {}) => {
  if (isComplexRuleMode(draft.stockPriceRuleMode)) {
    const complexPriceRuleId = Number(
      String(draft.stockPriceRuleMode).slice('complex:'.length)
    );
    return {
      shared: { manualBaseCost: toNullableNumber(draft.manualBaseCost) },
      branch: {
        priceRule: { type: 'complex', complexPriceRuleId },
      },
    };
  }
  const stockPriceRuleMode = STOCK_RULE_MODES.includes(draft.stockPriceRuleMode)
    ? draft.stockPriceRuleMode
    : null;

  return {
    shared: {
      manualBaseCost: toNullableNumber(draft.manualBaseCost),
    },
    branch: {
      stockPriceRule: stockPriceRuleMode
        ? {
          mode: stockPriceRuleMode,
          value: toNullableNumber(draft.stockPriceRuleValue),
        }
        : null,
    },
  };
};

const resolveCostForBasis = (entry, basis) => {
  if (!entry || typeof entry !== 'object') return null;
  const key = normalizeBasis(basis) === 'kg' ? 'perKg' : 'perSaleUnit';
  return toNullableNumber(entry[key] ?? entry.value);
};

export const resolveAdvancedCostAnalysis = (format = {}, productBasis = 'unit') => {
  const basis = normalizeBasis(productBasis);
  const costs = format.costs || {};
  const components = costs.costComponents || {};
  const shared = format.shared || {};
  const branch = format.branch || {};
  const baseCost = resolveCostForBasis(components.base || costs.baseCost, basis);
  const operationalCost = resolveCostForBasis(
    components.operational || costs.operationalCost,
    basis
  );
  const referenceCost = toNullableNumber(
    costs.referenceCost
      ?? costs.referenceCostRaw
      ?? (basis === 'kg' ? shared.estimatedCostPerKg : shared.estimatedCost)
      ?? shared.manualBaseCost
  );
  const sellingPrice = toNullableNumber(branch.sellingPrice);
  const marginAmount = sellingPrice !== null && referenceCost !== null
    ? sellingPrice - referenceCost
    : null;
  const marginPercent = marginAmount !== null && sellingPrice > 0
    ? (marginAmount / sellingPrice) * 100
    : null;
  const source = costs.referenceCostSource
    || components.base?.source
    || costs.baseCost?.source
    || (shared.manualBaseCost !== null && shared.manualBaseCost !== undefined ? 'manual_fallback' : null);

  const server = format.analysis && typeof format.analysis === 'object'
    ? format.analysis
    : null;
  const serverReferenceCost = toNullableNumber(
    server?.referenceCost?.saleUnitValue ?? server?.referenceCost?.basisValue
  );
  const serverSellingPrice = toNullableNumber(server?.sellingPrice);
  const effectiveReferenceCost = serverReferenceCost ?? referenceCost;
  const effectiveSellingPrice = serverSellingPrice ?? sellingPrice;
  const effectiveMarginAmount = effectiveSellingPrice !== null && effectiveReferenceCost !== null
    ? effectiveSellingPrice - effectiveReferenceCost
    : marginAmount;
  const effectiveMarginPercent = effectiveMarginAmount !== null && effectiveSellingPrice > 0
    ? (effectiveMarginAmount / effectiveSellingPrice) * 100
    : marginPercent;
  const ruleSellingPrice = toNullableNumber(
    server?.stockPriceRule?.sellingPrice ?? server?.stockPriceRule?.recommendedSellingPrice
  );
  const recommendedSellingPrice = toNullableNumber(server?.recommendedSellingPrice);
  const pendingReasons = [];
  const serverPending = server?.pendingReasons || server?.pending || [];
  (Array.isArray(serverPending) ? serverPending : [serverPending]).filter(Boolean).forEach((reason) => {
    pendingReasons.push(typeof reason === 'string' ? reason : reason.message || reason.code);
  });
  if (server?.stockPriceRule?.error) pendingReasons.push(server.stockPriceRule.error);
  if (effectiveReferenceCost === null) pendingReasons.push('missing_reference_cost');
  if (basis === 'kg' && server && !toNullableNumber(server.kgPerSaleUnit)) {
    pendingReasons.push('missing_kg_per_sale_unit');
  }

  return {
    basis,
    baseCost,
    operationalCost,
    referenceCost: effectiveReferenceCost,
    sellingPrice: effectiveSellingPrice,
    marginAmount: effectiveMarginAmount,
    marginPercent: effectiveMarginPercent,
    source: server?.referenceCost?.source || source,
    ruleSellingPrice,
    recommendedSellingPrice,
    warnings: Array.isArray(server?.warnings) ? server.warnings : [],
    pendingReasons: Array.from(new Set(pendingReasons.filter(Boolean))),
    serverAuthoritative: Boolean(server),
  };
};

const pendingReasonLabel = (reason) => {
  if (reason && typeof reason === 'object') return reason.message || reason.code || 'Cálculo pendiente';
  const labels = {
    missing_reference_cost: 'Falta un costo de referencia válido.',
    missing_kg_per_sale_unit: 'Falta el peso por unidad para convertir la base por kg.',
    invalid_stock_price_rule: 'La regla de precio no es válida.',
    missing_stock_price_rule: 'No hay una regla de precio configurada.',
  };
  return labels[String(reason || '')] || String(reason || 'Cálculo pendiente');
};

const formatPolicyValue = (value) => currency(value);

const ScopeBadge = ({ children, message, tone = 'indigo' }) => {
  const tones = {
    indigo: 'border-indigo-200 bg-indigo-50 text-indigo-700',
    blue: 'border-blue-200 bg-blue-50 text-blue-700',
    gray: 'border-gray-200 bg-gray-50 text-gray-600',
  };
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${tones[tone]}`}>
      {children}
      <InfoTooltip message={message} label={`Más información sobre el alcance ${children}`} />
    </span>
  );
};

const FieldError = ({ id, children }) => (
  children ? <p id={id} className="mt-1 text-xs text-red-600">{children}</p> : null
);

const formatTitle = (format) => {
  const identity = format.identity || {};
  return identity.ProductFormat?.name
    || identity.productFormatName
    || identity.productFormatCode
    || identity.clientType
    || 'Formato';
};

const formatSubtitle = (format) => {
  const identity = format.identity || {};
  return [identity.metricUnit, identity.description].filter(Boolean).join(' · ');
};

const FormatSettingsCard = ({
  format,
  activeBranchId,
  productBasis,
  canUpdatePrices,
  highlighted,
  onSaved,
  complexPriceRules = [],
}) => {
  const id = String(format.varietyPriceId);
  const [draft, setDraft] = useState(() => buildAdvancedPricingSettingsDraft(format));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');
  const formatRef = useRef(format);
  formatRef.current = format;
  const serverConfigurationKey = JSON.stringify([
    format.varietyPriceId,
    format.shared?.manualBaseCost ?? null,
    format.branch?.priceRule ?? null,
    format.branch?.stockPriceRule?.mode ?? null,
    format.branch?.stockPriceRule?.value ?? null,
  ]);
  const analysis = useMemo(
    () => resolveAdvancedCostAnalysis(format, productBasis),
    [format, productBasis]
  );
  const selectedComplexId = isComplexRuleMode(draft.stockPriceRuleMode)
    ? Number(String(draft.stockPriceRuleMode).slice('complex:'.length))
    : null;
  const selectedComplexRule = findComplexPriceRule(complexPriceRules, selectedComplexId);
  const selectedComplexResolution = resolvePriceRuleSelection(
    selectedComplexId ? { type: 'complex', complexPriceRuleId: selectedComplexId } : null,
    {
      complexPriceRules,
      referenceCost: analysis.referenceCost,
      basis: productBasis,
    }
  );
  const selectedMatchedRange = format.analysis?.priceRule?.selection?.type === 'complex'
    && Number(format.analysis.priceRule.selection.complexPriceRuleId) === selectedComplexId
    ? format.analysis.priceRule.matchedRange || selectedComplexResolution.matchedRange
    : selectedComplexResolution.matchedRange;

  useEffect(() => {
    setDraft(buildAdvancedPricingSettingsDraft(formatRef.current));
  }, [serverConfigurationKey]);

  const patchDraft = (key, value) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
    setStatus('');
  };

  const handleTypeChange = (typeKey, valueKey, value) => {
    setDraft((current) => ({
      ...current,
      [typeKey]: value,
      [valueKey]: value && !isComplexRuleMode(value) ? current[valueKey] : '',
    }));
    setErrors({});
    setStatus('');
  };

  const handlePairedValueChange = (typeKey, valueKey, value) => {
    const cleared = value === '' || value === null || value === undefined;
    setDraft((current) => ({
      ...current,
      [typeKey]: cleared ? '' : current[typeKey],
      [valueKey]: cleared ? '' : value,
    }));
    setErrors((current) => {
      if (!current[typeKey] && !current[valueKey]) return current;
      const next = { ...current };
      delete next[typeKey];
      delete next[valueKey];
      return next;
    });
    setStatus('');
  };

  const handleSave = async () => {
    const nextErrors = validateAdvancedPricingSettingsDraft(draft);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      setStatus('Revisa los campos marcados antes de guardar.');
      return;
    }

    setSaving(true);
    setErrors({});
    setStatus('');
    const payload = buildAdvancedPricingSettingsPayload(draft);
    const requestBranchId = String(activeBranchId || '');
    try {
      const response = await updatePriceListFormatSettings(
        requestBranchId,
        format.varietyPriceId,
        payload
      );
      onSaved(format.varietyPriceId, payload, response, requestBranchId);
      setStatus('Configuración guardada.');
    } catch (requestError) {
      setStatus(
        requestError?.response?.data?.message
          || requestError?.message
          || 'No se pudo guardar la configuración.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <article
      id={`advanced-pricing-format-${id}`}
      tabIndex={highlighted ? -1 : undefined}
      data-highlighted={highlighted ? 'true' : undefined}
      className={`rounded-xl border bg-white p-3 shadow-sm outline-none transition sm:p-4 ${
        highlighted
          ? 'border-blue-400 ring-2 ring-blue-100 focus:ring-4'
          : 'border-gray-200'
      }`}
    >
      <div className="flex flex-col gap-2 border-b border-gray-100 pb-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-950">{formatTitle(format)}</h4>
          {formatSubtitle(format) && (
            <p className="mt-0.5 text-xs text-gray-500">{formatSubtitle(format)}</p>
          )}
          <p className="mt-1 text-[11px] text-gray-400">ID estable: {id}</p>
        </div>
        <span className="self-start rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-[11px] font-medium text-gray-600">
          Base {basisLabel(productBasis)}
        </span>
      </div>

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <fieldset className="min-w-0 rounded-lg border border-indigo-100 bg-indigo-50/40 p-3">
          <legend className="px-1 text-xs font-semibold text-gray-800">
            <span className="inline-flex flex-wrap items-center gap-2">
              Costo
              <ScopeBadge
                message="Se comparte entre todas las sucursales de la empresa. Cambiarla aquí afecta este formato en toda la compañía."
              >
                Empresa
              </ScopeBadge>
            </span>
          </legend>

          <div className="mt-2 flex items-center gap-1">
            <label className="block text-xs font-medium text-gray-700" htmlFor={`manual-cost-${id}`}>
              Costo manual de respaldo
            </label>
            <InfoTooltip
              message="Se usa solo cuando no hay un costo automático válido. Déjalo vacío para priorizar listas de stock y compras."
              label="Qué es el costo manual de respaldo"
            />
          </div>
          <div>
            <div className="relative mt-1">
              <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm text-gray-400">$</span>
              <input
                id={`manual-cost-${id}`}
                type="number"
                inputMode="decimal"
                min="0"
                step="1"
                value={draft.manualBaseCost}
                onChange={(event) => patchDraft('manualBaseCost', event.target.value)}
                disabled={!canUpdatePrices || saving}
                aria-invalid={Boolean(errors.manualBaseCost)}
                aria-describedby={errors.manualBaseCost ? `manual-cost-error-${id}` : undefined}
                placeholder="Automático"
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-7 pr-3 text-sm disabled:bg-gray-100"
              />
            </div>
          </div>
          <FieldError id={`manual-cost-error-${id}`}>{errors.manualBaseCost}</FieldError>
          <p className="mt-1 text-[11px] text-gray-500">
            {draft.manualBaseCost === ''
              ? `Costo calculado actual: ${currency(analysis.referenceCost)}`
              : `Respaldo manual ${basisLabel(productBasis)}`}
          </p>
          <p className="mt-2 text-[11px] text-gray-500">
            Las listas de stock y las compras tienen prioridad; este valor solo se usa como respaldo.
          </p>
        </fieldset>

        <fieldset className="min-w-0 rounded-lg border border-blue-100 bg-blue-50/40 p-3">
            <legend className="px-1 text-xs font-semibold text-gray-800">
              <span className="inline-flex flex-wrap items-center gap-2">
                Regla de precio
                <ScopeBadge
                  tone="blue"
                  message="Solo aplica a la sucursal seleccionada. El sistema la usa para proponer precios cuando cambia el costo de referencia."
                >
                  Sucursal
                </ScopeBadge>
              </span>
            </legend>
            <div className="mt-2 grid grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)] gap-2">
              <select
                aria-label={`Regla de precio para ${formatTitle(format)}`}
                value={draft.stockPriceRuleMode}
                onChange={(event) => handleTypeChange('stockPriceRuleMode', 'stockPriceRuleValue', event.target.value)}
                disabled={!canUpdatePrices || saving}
                aria-invalid={Boolean(errors.stockPriceRuleMode)}
                aria-describedby={errors.stockPriceRuleMode ? `rule-value-error-${id}` : undefined}
                className="min-w-0 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm disabled:bg-gray-100"
              >
                <option value="">Sin regla</option>
                <option value="markup_amount">Sumar monto al costo</option>
                <option value="markup_percent">Sumar porcentaje al costo</option>
                <option value="fixed_price">Precio fijo recomendado</option>
                {complexPriceRules.length > 0 && (
                  <optgroup label="Reglas complejas">
                    {complexPriceRules.map((rule) => (
                      <option key={rule.id} value={`complex:${rule.id}`}>{rule.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
              {!selectedComplexId && <input
                type="number"
                inputMode="decimal"
                min="0"
                step={draft.stockPriceRuleMode === 'markup_percent' ? '0.01' : '1'}
                aria-label={`Valor de regla de precio para ${formatTitle(format)}`}
                value={draft.stockPriceRuleValue}
                onChange={(event) => handlePairedValueChange(
                  'stockPriceRuleMode',
                  'stockPriceRuleValue',
                  event.target.value
                )}
                disabled={!canUpdatePrices || saving || !draft.stockPriceRuleMode}
                aria-invalid={Boolean(errors.stockPriceRuleValue)}
                aria-describedby={errors.stockPriceRuleValue ? `rule-value-error-${id}` : undefined}
                placeholder={draft.stockPriceRuleMode === 'markup_percent' ? '%' : '$'}
                className="min-w-0 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm disabled:bg-gray-100"
              />}
            </div>
            {selectedComplexId && (
              <p className="mt-2 text-[11px] font-medium leading-relaxed text-blue-700">
                <span className="font-semibold">{selectedComplexRule?.name || 'Regla compleja'}</span>
                {' · '}
                {selectedMatchedRange
                  ? formatComplexRangeLabel(
                    selectedMatchedRange,
                    Number(selectedMatchedRange.sortOrder || 0)
                  )
                  : 'Sin rango efectivo hasta disponer de un costo válido.'}
              </p>
            )}
            <FieldError id={`rule-value-error-${id}`}>
              {errors.stockPriceRuleMode || errors.stockPriceRuleValue}
            </FieldError>
            <p className="mt-2 text-[11px] text-gray-500">
              Se calcula con la base del producto ({basisLabel(productBasis)}); la base no se cambia por formato.
            </p>
        </fieldset>
      </div>

      <section aria-label={`Análisis de ${formatTitle(format)}`} className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h5 className="text-xs font-semibold text-gray-800">Análisis actual</h5>
              <ScopeBadge
                tone="gray"
                message="Estos valores se calculan con la configuración y los datos operativos actuales. Se editan desde sus pantallas propietarias."
              >
                Solo lectura
              </ScopeBadge>
            </div>
            <p className="mt-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">
              Relación de cálculo
            </p>
            <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
              <div>
                <dt className="text-gray-500">1. Costo de referencia</dt>
                <dd className="mt-0.5 font-semibold text-gray-900">{formatPolicyValue(analysis.referenceCost)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">2. Resultado de la regla</dt>
                <dd className="mt-0.5 font-semibold text-gray-900">{formatPolicyValue(analysis.ruleSellingPrice)}</dd>
              </div>
            </dl>
            <p className="mt-2 text-[11px] text-gray-500">
              {sourceLabel(analysis.source)} · Recomendación final: {formatPolicyValue(analysis.recommendedSellingPrice)}.
            </p>
            {analysis.pendingReasons.length > 0 && (
              <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800">
                <p className="font-semibold">Cálculos pendientes</p>
                <ul className="mt-1 list-disc space-y-0.5 pl-4">
                  {analysis.pendingReasons.map((reason) => (
                    <li key={String(reason)}>{pendingReasonLabel(reason)}</li>
                  ))}
                </ul>
              </div>
            )}
            {analysis.warnings.length > 0 && (
              <ul className="mt-2 space-y-1 text-[11px] text-amber-800">
                {analysis.warnings.map((warning, index) => (
                  <li key={warning.code || index}>{warning.message || pendingReasonLabel(warning)}</li>
                ))}
              </ul>
            )}
      </section>

      <div className="mt-4 flex flex-col gap-2 border-t border-gray-100 pt-3 sm:flex-row sm:items-center sm:justify-between">
        <p aria-live="polite" className={`text-xs ${status.includes('guardada') ? 'text-emerald-700' : 'text-red-600'}`}>
          {status}
        </p>
        {canUpdatePrices ? (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !activeBranchId}
            className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
          >
            {saving ? 'Guardando formato…' : 'Guardar este formato'}
          </button>
        ) : (
          <span className="text-xs font-medium text-gray-500">Sin permiso para editar precios</span>
        )}
      </div>
    </article>
  );
};

const ProductPricingBasisControl = ({
  activeBranchId,
  product,
  canUpdatePrices,
  onSaved,
}) => {
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  const handleChange = async (event) => {
    const value = event.target.value;
    const requestBranchId = String(activeBranchId || '');
    setSaving(true);
    setStatus('');
    try {
      await updateProductPriceListConfiguration(requestBranchId, product.id, {
        pricingBasisOverride: value || null,
      });
      setStatus('Base actualizada.');
      await onSaved(requestBranchId);
    } catch (requestError) {
      setStatus(
        requestError?.response?.data?.message
          || requestError?.message
          || 'No se pudo actualizar la base.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-w-[12rem] rounded-lg border border-indigo-100 bg-indigo-50/60 p-2">
      <div className="flex items-center gap-1">
        <label htmlFor={`product-pricing-basis-${product.id}`} className="text-[11px] font-semibold text-gray-700">
          Base de cálculo del producto
        </label>
        <InfoTooltip
          message="Define si costos, márgenes y reglas compartidas se expresan por unidad de venta o por kilogramo. Cambiarla puede convertir la configuración existente."
          label={`Qué es la base de cálculo de ${product.name}`}
        />
      </div>
      <div className="mt-1 flex items-center gap-2">
        <select
          id={`product-pricing-basis-${product.id}`}
          aria-label={`Base de cálculo de ${product.name}`}
          value={product.pricingBasisOverride || ''}
          onChange={handleChange}
          disabled={!canUpdatePrices || saving}
          className="min-w-0 flex-1 rounded border border-indigo-200 bg-white px-2 py-1.5 text-xs disabled:bg-gray-100"
        >
          <option value="">Heredada ({basisLabel(product.effectivePricingBasis)})</option>
          <option value="unit">Por unidad de venta</option>
          <option value="kg">Por kilogramo</option>
        </select>
        <ScopeBadge
          message="La base del producto y sus conversiones se comparten entre todas las sucursales."
        >
          Empresa
        </ScopeBadge>
      </div>
      {status && (
        <p aria-live="polite" className={`mt-1 text-[11px] ${status.includes('actualizada') ? 'text-emerald-700' : 'text-red-600'}`}>
          {status}
        </p>
      )}
    </div>
  );
};

const getVarietyFormatCodes = (variety) => Array.from(new Set(
  (variety.formats || []).map((format) => (
    format.identity?.productFormatCode
      || format.identity?.ProductFormat?.code
      || format.identity?.clientType
  )).filter(Boolean)
));

const humanizeFormatCode = (code) => {
  const normalized = String(code || '').trim();
  if (normalized === 'detalle') return 'Detalle';
  if (normalized === 'mayorista') return 'Mayorista';
  return normalized || 'Formato';
};

const VarietyAutomaticFormatSelection = ({
  activeBranchId,
  variety,
  canUpdatePrices,
  onSaved,
}) => {
  const rule = variety.priceAutoRuleOverride;
  const codes = useMemo(() => getVarietyFormatCodes(variety), [variety]);
  const ruleConfigurationRef = useRef({ rule, codes });
  ruleConfigurationRef.current = { rule, codes };
  const ruleConfigurationKey = JSON.stringify([rule || null, codes]);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState('');

  useEffect(() => {
    if (!editing) return;
    const current = ruleConfigurationRef.current;
    setDraft({
      inherit: !current.rule,
      mode: current.rule?.mode === 'subtotal' ? 'subtotal' : 'quantity',
      thresholds: current.codes.reduce((result, code) => ({
        ...result,
        [code]: current.rule?.thresholds?.[code] ?? '',
      }), {}),
    });
  }, [editing, ruleConfigurationKey]);

  const beginEdit = () => {
    setStatus('');
    setEditing(true);
  };

  const handleSave = async () => {
    if (!draft) return;
    const thresholds = {};
    for (const [code, rawValue] of Object.entries(draft.thresholds || {})) {
      if (rawValue === '' || rawValue === null) continue;
      const value = Number(rawValue);
      if (!Number.isFinite(value) || value < 0) {
        setStatus('Los umbrales deben ser números mayores o iguales a 0.');
        return;
      }
      thresholds[code] = value;
    }

    const priceAutoRuleOverride = draft.inherit
      ? null
      : { mode: draft.mode, thresholds };
    setSaving(true);
    setStatus('');
    const requestBranchId = String(activeBranchId || '');
    try {
      const response = await updateVarietyPriceListConfiguration(requestBranchId, variety.id, {
        priceAutoRuleOverride,
      });
      onSaved(
        response?.data?.priceAutoRuleOverride ?? priceAutoRuleOverride,
        requestBranchId
      );
      setEditing(false);
      setStatus('Selección automática guardada.');
    } catch (requestError) {
      setStatus(
        requestError?.response?.data?.message
          || requestError?.message
          || 'No se pudo guardar la selección automática.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-lg border border-violet-200 bg-violet-50/60 p-3" aria-label={`Selección automática de formato de ${variety.name}`}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-xs font-semibold text-gray-900">Selección automática de formato</h4>
            <ScopeBadge
              message="Esta regla pertenece a la variedad y se comparte entre sucursales. No cambia el cálculo del precio por stock."
            >
              Empresa
            </ScopeBadge>
            <InfoTooltip
              message="Decide cuándo una venta cambia automáticamente de formato según cantidad o subtotal. Es independiente de la regla de precio de cada formato."
              label={`Qué es la selección automática de formato para ${variety.name}`}
            />
          </div>
          <p className="mt-1 text-[11px] text-gray-600">
            {rule
              ? `${rule.mode === 'subtotal' ? 'Por subtotal' : 'Por cantidad'} · configuración propia de la variedad`
              : 'Hereda los umbrales globales.'}
          </p>
        </div>
        {canUpdatePrices && !editing && (
          <button type="button" onClick={beginEdit} className="self-start text-xs font-semibold text-violet-700 hover:underline">
            Editar selección
          </button>
        )}
      </div>

      {editing && draft && (
        <div className="mt-3 grid gap-3 border-t border-violet-100 pt-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="flex items-center gap-2 text-xs font-medium text-gray-700">
            <input
              type="checkbox"
              checked={draft.inherit}
              onChange={(event) => setDraft((current) => ({ ...current, inherit: event.target.checked }))}
              disabled={saving}
            />
            Heredar regla global
          </label>
          {!draft.inherit && (
            <>
              <label className="text-xs font-medium text-gray-700">
                Criterio
                <select
                  value={draft.mode}
                  onChange={(event) => setDraft((current) => ({ ...current, mode: event.target.value }))}
                  disabled={saving}
                  className="mt-1 block w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm"
                >
                  <option value="quantity">Cantidad por variedad</option>
                  <option value="subtotal">Subtotal del documento</option>
                </select>
              </label>
              {codes.map((code) => (
                <label key={code} className="text-xs font-medium text-gray-700">
                  Umbral {humanizeFormatCode(code)}
                  <input
                    type="number"
                    min="0"
                    value={draft.thresholds[code] ?? ''}
                    onChange={(event) => setDraft((current) => ({
                      ...current,
                      thresholds: { ...current.thresholds, [code]: event.target.value },
                    }))}
                    disabled={saving}
                    className="mt-1 block w-full rounded border border-gray-300 bg-white px-2 py-1.5 text-sm"
                  />
                </label>
              ))}
            </>
          )}
          <div className="flex items-end gap-2 sm:col-span-2 lg:col-span-3">
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="rounded bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
            >
              {saving ? 'Guardando…' : 'Guardar selección'}
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              disabled={saving}
              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700"
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
      {status && (
        <p aria-live="polite" className={`mt-2 text-[11px] ${status.includes('guardada') ? 'text-emerald-700' : 'text-red-600'}`}>
          {status}
        </p>
      )}
    </section>
  );
};

const formatKey = (productId, varietyId) => `${productId}:${varietyId}`;

const findFormatOwner = (catalog, targetFormatId) => {
  if (!targetFormatId) return null;
  for (const product of catalog) {
    for (const variety of product.varieties) {
      const format = variety.formats.find(
        (entry) => String(entry.varietyPriceId) === String(targetFormatId)
      );
      if (format) return { product, variety, format };
    }
  }
  return null;
};

const findVarietyOwner = (catalog, targetVarietyId) => {
  if (!targetVarietyId) return null;
  for (const product of catalog) {
    const variety = product.varieties.find(
      (entry) => String(entry.id) === String(targetVarietyId)
    );
    if (variety) return { product, variety };
  }
  return null;
};

const filterCatalog = (catalog, search) => {
  const term = String(search || '').trim().toLocaleLowerCase('es');
  if (!term) return catalog;
  return catalog.reduce((products, product) => {
    const productMatches = `${product.name} ${product.categoryName}`
      .toLocaleLowerCase('es')
      .includes(term);
    const varieties = product.varieties.reduce((items, variety) => {
      const varietyMatches = variety.name.toLocaleLowerCase('es').includes(term);
      const formats = productMatches || varietyMatches
        ? variety.formats
        : variety.formats.filter((format) => (
          `${formatTitle(format)} ${formatSubtitle(format)}`
            .toLocaleLowerCase('es')
            .includes(term)
        ));
      if (formats.length) items.push({ ...variety, formats });
      return items;
    }, []);
    if (varieties.length) products.push({ ...product, varieties });
    return products;
  }, []);
};

const mergeSavedFormat = (catalog, varietyPriceId, payload, response) => {
  const responseData = response?.data?.data || response?.data || response || {};
  const responseShared = responseData.shared || responseData.format?.shared || {};
  const responseBranch = responseData.branch || responseData.format?.branch || {};
  return catalog.map((product) => {
    let productChanged = false;
    const varieties = product.varieties.map((variety) => {
      const formatIndex = variety.formats.findIndex(
        (format) => String(format.varietyPriceId) === String(varietyPriceId)
      );
      if (formatIndex < 0) return variety;
      productChanged = true;
      const format = variety.formats[formatIndex];
      const formats = [...variety.formats];
      const incomingBranch = { ...(payload.branch || {}), ...responseBranch };
      const incomingSelection = Object.hasOwn(incomingBranch, 'priceRule')
        ? normalizePriceRuleSelection(incomingBranch.priceRule)
        : Object.hasOwn(incomingBranch, 'stockPriceRule')
          ? normalizePriceRuleSelection(null, incomingBranch.stockPriceRule)
          : normalizePriceRuleSelection(
            format.branch?.priceRule,
            format.branch?.stockPriceRule
          );
      formats[formatIndex] = {
        ...format,
        shared: { ...format.shared, ...payload.shared, ...responseShared },
        branch: {
          ...format.branch,
          ...incomingBranch,
          priceRule: incomingSelection,
          stockPriceRule: incomingSelection?.type === 'simple'
            ? incomingSelection.rule
            : null,
        },
        costs: responseData.costs || responseData.format?.costs || format.costs,
        analysis: responseData.analysis || responseData.format?.analysis || format.analysis,
      };
      return { ...variety, formats };
    });
    return productChanged ? { ...product, varieties } : product;
  });
};

const AdvancedPricingSettingsPanel = ({
  activeBranchId,
  canReadPrices = true,
  canUpdatePrices = false,
  onOpenOperationalList,
}) => {
  const [searchParams] = useSearchParams();
  const targetFormatId = searchParams.get('varietyPriceId');
  const requestedVarietyId = searchParams.get('varietyId');
  const targetKind = targetFormatId ? 'format' : requestedVarietyId ? 'variety' : '';
  const targetId = targetFormatId || requestedVarietyId || '';
  const targetKey = targetKind && targetId ? `${targetKind}:${targetId}` : '';
  const [catalog, setCatalog] = useState([]);
  const [complexPriceRules, setComplexPriceRules] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [targetMissing, setTargetMissing] = useState(false);
  const [expandedProducts, setExpandedProducts] = useState({});
  const [expandedVarieties, setExpandedVarieties] = useState({});
  const requestIdRef = useRef(0);
  const loadedBranchIdRef = useRef('');
  const focusedTargetRef = useRef('');
  const expansionContextRef = useRef('');
  const eventRefreshTimerRef = useRef(null);
  const activeBranchIdRef = useRef(String(activeBranchId || ''));
  activeBranchIdRef.current = String(activeBranchId || '');

  const loadCatalog = useCallback(async () => {
    if (!activeBranchId || !canReadPrices) {
      setCatalog([]);
      setComplexPriceRules([]);
      loadedBranchIdRef.current = '';
      setLoading(false);
      return;
    }
    const nextBranchId = String(activeBranchId);
    if (activeBranchIdRef.current !== nextBranchId) return;
    const changingBranch = loadedBranchIdRef.current !== nextBranchId;
    if (changingBranch) {
      setCatalog([]);
      setTargetMissing(false);
    }
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    setError('');
    try {
      const [response, complexRulesResult] = await Promise.all([
        getPriceListConfigurations(activeBranchId),
        Promise.resolve(getComplexPriceRules())
          .then((rules) => ({ rules }))
          .catch(() => ({ rules: null })),
      ]);
      if (requestId !== requestIdRef.current
        || activeBranchIdRef.current !== nextBranchId) return;
      const nextCatalog = normalizeAdvancedPricingCatalog(response);
      const owner = targetKind === 'format'
        ? findFormatOwner(nextCatalog, targetId)
        : targetKind === 'variety'
          ? findVarietyOwner(nextCatalog, targetId)
          : null;
      setCatalog(nextCatalog);
      if (Array.isArray(complexRulesResult.rules)) {
        setComplexPriceRules(complexRulesResult.rules);
      }
      loadedBranchIdRef.current = nextBranchId;
      setTargetMissing(Boolean(targetKey && !owner));
      const expansionContext = `${activeBranchId}:${targetKey}`;
      if (expansionContextRef.current !== expansionContext) {
        expansionContextRef.current = expansionContext;
        if (owner) {
          setExpandedProducts({ [String(owner.product.id)]: true });
          setExpandedVarieties({ [formatKey(owner.product.id, owner.variety.id)]: true });
        } else {
          const firstProduct = nextCatalog[0];
          const firstVariety = firstProduct?.varieties?.[0];
          setExpandedProducts(firstProduct ? { [String(firstProduct.id)]: true } : {});
          setExpandedVarieties(
            firstProduct && firstVariety
              ? { [formatKey(firstProduct.id, firstVariety.id)]: true }
              : {}
          );
        }
      }
    } catch (requestError) {
      if (requestId !== requestIdRef.current
        || activeBranchIdRef.current !== nextBranchId) return;
      if (changingBranch) setCatalog([]);
      loadedBranchIdRef.current = nextBranchId;
      setError(
        requestError?.response?.data?.message
          || requestError?.message
          || 'No se pudo cargar la configuración avanzada de precios.'
      );
    } finally {
      if (requestId === requestIdRef.current
        && activeBranchIdRef.current === nextBranchId) setLoading(false);
    }
  }, [activeBranchId, canReadPrices, targetId, targetKey, targetKind]);

  useEffect(() => {
    setTargetMissing(false);
    if (targetKey) setSearch('');
  }, [targetKey]);

  useEffect(() => {
    focusedTargetRef.current = '';
    loadCatalog();
    return () => {
      requestIdRef.current += 1;
    };
  }, [loadCatalog]);

  const scheduleEventRefresh = useCallback(() => {
    if (!activeBranchId || !canReadPrices) return;
    if (eventRefreshTimerRef.current) clearTimeout(eventRefreshTimerRef.current);
    eventRefreshTimerRef.current = setTimeout(() => {
      eventRefreshTimerRef.current = null;
      loadCatalog();
    }, 250);
  }, [activeBranchId, canReadPrices, loadCatalog]);

  useProductEvents(activeBranchId, scheduleEventRefresh);
  usePurchaseEvents(activeBranchId, scheduleEventRefresh, {
    enabled: Boolean(activeBranchId && canReadPrices),
  });

  useEffect(() => () => {
    if (eventRefreshTimerRef.current) clearTimeout(eventRefreshTimerRef.current);
  }, []);

  useEffect(() => {
    if (!targetKey || loading || focusedTargetRef.current === targetKey) return;
    const varietyOwner = targetKind === 'variety'
      ? findVarietyOwner(catalog, targetId)
      : null;
    const elementId = targetKind === 'format'
      ? `advanced-pricing-format-${targetId}`
      : varietyOwner
        ? `advanced-pricing-variety-${varietyOwner.product.id}-${varietyOwner.variety.id}`
        : '';
    if (!elementId) return;
    const target = document.getElementById(elementId);
    if (!target) return;
    focusedTargetRef.current = targetKey;
    target.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    target.focus({ preventScroll: true });
  }, [catalog, loading, targetId, targetKey, targetKind]);

  const visibleCatalog = useMemo(() => filterCatalog(catalog, search), [catalog, search]);
  const searching = Boolean(search.trim());
  const awaitingActiveBranch = loadedBranchIdRef.current !== String(activeBranchId || '');
  const totalFormats = useMemo(
    () => catalog.reduce(
      (total, product) => total + product.varieties.reduce(
        (subtotal, variety) => subtotal + variety.formats.length,
        0
      ),
      0
    ),
    [catalog]
  );

  if (!activeBranchId) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        Selecciona una sucursal para configurar precios avanzados.
      </div>
    );
  }

  if (!canReadPrices) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
        No tienes permisos para ver la configuración de precios.
      </div>
    );
  }

  return (
    <section className="min-w-0 space-y-4" aria-labelledby="advanced-pricing-heading">
      <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 id="advanced-pricing-heading" className="text-lg font-semibold text-gray-950">
                Configuración de precios
              </h2>
              <span className="rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                {totalFormats} formatos
              </span>
            </div>
            <p className="mt-1 max-w-3xl text-sm text-gray-600">
              Define costos de respaldo y reglas automáticas por formato. Los cambios operativos de la lista se administran en su vista dedicada.
            </p>
          </div>
          {onOpenOperationalList && (
            <button
              type="button"
              onClick={onOpenOperationalList}
              className="inline-flex w-full shrink-0 items-center justify-center gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100 lg:w-auto"
            >
              Abrir lista operativa
              <FiExternalLink aria-hidden="true" />
            </button>
          )}
        </div>

        <label className="relative mt-4 block" htmlFor="advanced-pricing-search">
          <span className="sr-only">Buscar producto, variedad o formato</span>
          <FiSearch aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            id="advanced-pricing-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar producto, variedad o formato"
            className="w-full rounded-md border border-gray-300 py-2 pl-9 pr-3 text-sm"
          />
        </label>
      </div>

      {!awaitingActiveBranch && targetMissing && (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800" role="status">
          {targetKind === 'variety'
            ? 'La variedad solicitada no está disponible en la sucursal seleccionada.'
            : 'El formato solicitado no está disponible en la sucursal seleccionada.'}
        </p>
      )}
      {!awaitingActiveBranch && error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert">
          <p>{error}</p>
          <button type="button" onClick={() => loadCatalog()} className="mt-2 font-semibold underline">
            Reintentar
          </button>
        </div>
      )}
      {(loading || awaitingActiveBranch) && <InlineLoading label="Cargando configuración de precios…" />}

      {!awaitingActiveBranch
        && (!loading || catalog.length > 0)
        && (!error || catalog.length > 0)
        && visibleCatalog.map((product) => {
        const productId = String(product.id);
        const productExpanded = searching || Boolean(expandedProducts[productId]);
        const productRegionId = `advanced-pricing-product-${productId}`;
        return (
          <article key={productId} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="flex flex-col gap-2 p-3 sm:flex-row sm:items-center sm:justify-between">
              <button
                type="button"
                aria-expanded={productExpanded}
                aria-controls={productRegionId}
                onClick={() => setExpandedProducts((current) => ({
                  ...current,
                  [productId]: !productExpanded,
                }))}
                className="flex min-w-0 flex-1 items-center justify-between gap-3 rounded-md px-1 py-1 text-left hover:bg-gray-50"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-gray-950">{product.name}</span>
                  <span className="mt-0.5 block text-xs text-gray-500">{product.categoryName}</span>
                </span>
                {productExpanded ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
              </button>
              <ProductPricingBasisControl
                activeBranchId={activeBranchId}
                product={product}
                canUpdatePrices={canUpdatePrices}
                onSaved={(savedBranchId) => {
                  if (activeBranchIdRef.current !== String(savedBranchId)) return undefined;
                  return loadCatalog();
                }}
              />
            </div>

            {productExpanded && (
              <div id={productRegionId} className="space-y-3 border-t border-gray-100 bg-gray-50/50 p-3 sm:p-4">
                <p className="text-[11px] text-gray-500">
                  Base de cálculo del producto: {basisLabel(product.effectivePricingBasis)} · {product.pricingBasisOverride ? 'Configurada en el producto' : 'Heredada de su categoría'}.
                </p>
                {product.varieties.map((variety) => {
                  const key = formatKey(product.id, variety.id);
                  const varietyExpanded = searching || Boolean(expandedVarieties[key]);
                  const varietyElementId = `advanced-pricing-variety-${product.id}-${variety.id}`;
                  const varietyRegionId = `advanced-pricing-variety-panel-${product.id}-${variety.id}`;
                  const varietyHighlighted = targetKind === 'variety'
                    && String(targetId) === String(variety.id);
                  return (
                    <section
                      key={key}
                      id={varietyElementId}
                      tabIndex={varietyHighlighted ? -1 : undefined}
                      data-highlighted={varietyHighlighted ? 'true' : undefined}
                      aria-label={varietyHighlighted ? `Variedad solicitada: ${variety.name}` : undefined}
                      className={`overflow-hidden rounded-lg border bg-white outline-none transition ${
                        varietyHighlighted
                          ? 'relative z-[1] border-blue-400 ring-2 ring-blue-100 focus:ring-4'
                          : 'border-gray-200'
                      }`}
                    >
                      <button
                        type="button"
                        aria-expanded={varietyExpanded}
                        aria-controls={varietyRegionId}
                        onClick={() => setExpandedVarieties((current) => ({
                          ...current,
                          [key]: !varietyExpanded,
                        }))}
                        className="flex w-full min-w-0 items-center justify-between gap-3 px-3 py-2.5 text-left hover:bg-gray-50"
                      >
                        <span className="min-w-0 truncate text-sm font-semibold text-gray-800">{variety.name}</span>
                        <span className="flex shrink-0 items-center gap-2 text-xs text-gray-500">
                          {variety.formats.length} formatos
                          {varietyExpanded ? <FiChevronDown aria-hidden="true" /> : <FiChevronRight aria-hidden="true" />}
                        </span>
                      </button>
                      {varietyExpanded && (
                        <div id={varietyRegionId} className="space-y-3 border-t border-gray-100 bg-gray-50 p-3">
                          <VarietyAutomaticFormatSelection
                            activeBranchId={activeBranchId}
                            variety={variety}
                            canUpdatePrices={canUpdatePrices}
                            onSaved={(priceAutoRuleOverride, savedBranchId) => {
                              if (activeBranchIdRef.current !== String(savedBranchId)) return;
                              setCatalog((current) => current.map((catalogProduct) => (
                                String(catalogProduct.id) !== String(product.id)
                                  ? catalogProduct
                                  : {
                                    ...catalogProduct,
                                    varieties: catalogProduct.varieties.map((catalogVariety) => (
                                      String(catalogVariety.id) !== String(variety.id)
                                        ? catalogVariety
                                        : { ...catalogVariety, priceAutoRuleOverride }
                                    )),
                                  }
                              )));
                            }}
                          />
                          <div className="grid gap-3 2xl:grid-cols-2">
                            {variety.formats.map((format) => (
                              <FormatSettingsCard
                                key={format.varietyPriceId}
                                format={format}
                                activeBranchId={activeBranchId}
                                productBasis={product.effectivePricingBasis}
                                canUpdatePrices={canUpdatePrices}
                                highlighted={targetKind === 'format'
                                  && String(targetId) === String(format.varietyPriceId)}
                                complexPriceRules={complexPriceRules}
                                onSaved={(varietyPriceId, payload, response, savedBranchId) => {
                                  if (activeBranchIdRef.current !== String(savedBranchId)) return;
                                  setCatalog((current) => mergeSavedFormat(
                                    current,
                                    varietyPriceId,
                                    payload,
                                    response
                                  ));
                                }}
                              />
                            ))}
                            {!variety.formats.length && (
                              <p className="text-sm text-gray-500">Esta variedad todavía no tiene formatos.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            )}
          </article>
        );
      })}

      {!awaitingActiveBranch && !loading && !error && !visibleCatalog.length && (
        <div className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-8 text-center text-sm text-gray-500">
          {search ? 'No hay formatos que coincidan con la búsqueda.' : 'No hay formatos configurables en esta sucursal.'}
        </div>
      )}
    </section>
  );
};

export default AdvancedPricingSettingsPanel;
